"use client"

import { useEffect, useRef, useState } from "react"

import styles from "./hero.module.css"

const MOBILE_QUERY = "(max-width: 800px)"

type HeroVideoProps = {
  desktopSrc: string
  mobileSrc: string | null
}

type NetworkInformation = { saveData?: boolean }

/**
 * Video di sfondo caricato solo dopo l'idratazione e solo se l'utente non ha
 * chiesto meno movimento o meno dati. Sotto resta sempre il poster. Viene
 * messo in pausa quando la hero esce dal viewport: il blur sovrapposto si
 * ricalcola a ogni frame, quindi un video che gira fuori schermo costa GPU.
 */
export default function HeroVideo({ desktopSrc, mobileSrc }: HeroVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [src, setSrc] = useState<string | null>(null)
  const [readySrc, setReadySrc] = useState<string | null>(null)

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches
    const connection = (
      navigator as Navigator & { connection?: NetworkInformation }
    ).connection

    if (reducedMotion || connection?.saveData) {
      return
    }

    const mobileQuery = window.matchMedia(MOBILE_QUERY)
    const pick = () =>
      setSrc(mobileQuery.matches && mobileSrc ? mobileSrc : desktopSrc)

    pick()
    mobileQuery.addEventListener("change", pick)

    return () => mobileQuery.removeEventListener("change", pick)
  }, [desktopSrc, mobileSrc])

  useEffect(() => {
    const video = videoRef.current
    if (!src || !video || typeof IntersectionObserver === "undefined") {
      return
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        video.play().catch(() => undefined)
      } else {
        video.pause()
      }
    })
    observer.observe(video)

    return () => observer.disconnect()
  }, [src])

  if (!src) {
    return null
  }

  return (
    <video
      aria-hidden="true"
      autoPlay
      className={styles.video}
      data-ready={readySrc === src}
      disablePictureInPicture
      loop
      muted
      onCanPlay={() => setReadySrc(src)}
      playsInline
      preload="metadata"
      ref={videoRef}
      src={src}
    />
  )
}
